import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { UsersResults } from '@common/data-models/search-results/users-result';
import { ServiceResponse } from '@common/data-models/service/service-response';
import { UserStaffInfo } from '@common/data-models/service/user-staff-info';
import { InvitationStatusCodes } from '@common/utils/util';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { GlobalTablesService } from './global-tables-service';
import { NomWebServicesJson } from './nom-web-services-json';

@Injectable({ providedIn: 'root' })
export class TeamMembersService {
    nomWebServicesJson = inject(NomWebServicesJson);
    globalTablesService = inject(GlobalTablesService);

    public getTeamMembers(): Observable<ServiceResponse<{ userResults: UsersResults; security: any[] }>> {
        return this.nomWebServicesJson
            .getObservable<any>('/api/TeamMembers', {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.nomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            })
            .pipe(
                map(response => {
                    const res = response.body;
                    const userResults = new UsersResults();
                    for (const p of res.TeamMembers) {
                        const user = Object.assign(new UserStaffInfo(), p);
                        if (!user.InvitationStatus) {
                            user.InvitationStatus = InvitationStatusCodes.NEW;
                        }
                        userResults.addUser(user);
                    }
                    this.globalTablesService.populateSecurityRoles(res.Security);
                    return new ServiceResponse<{ userResults: UsersResults; security: any[] }>(true, {
                        userResults,
                        security: res.Security,
                    });
                }),
                catchError(err => {
                    this.nomWebServicesJson.showErrorModal({
                        message: 'Failed to load users!',
                        detailMessage: `Failed to load users! \n\rStackTrace: NomWebServicesJson.getTeamMembers(); \n\rError: ${err}`,
                    });
                    return of(new ServiceResponse<{ userResults: UsersResults; security: any[] }>(true, { userResults: new UsersResults(), security: [] }));
                })
            );
    }

    public async updateTeamMember(updateMember: UserStaffInfo): Promise<ServiceResponse<boolean>> {
        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.patch('/api/TeamMembers', updateMember, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.nomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (err) {
            this.nomWebServicesJson.showLockScreenError(err);
            return new ServiceResponse<boolean>(false, null);
        }
        const res = response.body;
        return new ServiceResponse<boolean>(true, res);
    }

    public async createTeamMember(updateMember: UserStaffInfo): Promise<ServiceResponse<boolean>> {
        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.put('/api/TeamMembers', updateMember, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.nomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (err) {
            this.nomWebServicesJson.showLockScreenError(err);
            return new ServiceResponse<boolean>(false, null);
        }
        const res = response.body;
        return new ServiceResponse<boolean>(true, res);
    }

    public async inviteTeamMember(userId: string, email: string): Promise<ServiceResponse<boolean>> {
        try {
            const replyUrl = window.location.href.split('#')[0];
            await this.nomWebServicesJson.post(
                '/api/Invitation',
                {
                    userId,
                    email,
                    replyUrl: `${replyUrl}#/login`,
                },
                {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        'X-ENPS-TOKEN': this.nomWebServicesJson.nomTokenId,
                    }),
                    responseType: 'json',
                    observe: 'response',
                }
            );
        } catch (err) {
            this.nomWebServicesJson.showLockScreenError(err);
            return new ServiceResponse<boolean>(false, null);
        }
        return new ServiceResponse<boolean>(true, '');
    }

    public async removeTeamMember(userID: string): Promise<ServiceResponse<boolean>> {
        try {
            await this.nomWebServicesJson.delete('/api/TeamMembers', {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.nomTokenId,
                }),
                responseType: 'json',
                params: {
                    userID,
                },
                observe: 'response',
            });
        } catch (err) {
            this.nomWebServicesJson.showLockScreenError(err);
            return new ServiceResponse<boolean>(false, false);
        }
        return new ServiceResponse<boolean>(true, true);
    }
}
